import styled from 'styled-components';

import Button from 'components/shared/Button';
import { simpleDateAtTime } from 'lib/date';
import { colors } from 'styles/theme/colors';
import { Body, BodySmall, LabelBold } from 'styles/typography';
import ErrorStatusIcon from 'svg/ErrorStatusIcon';

import { OperationalTaskCard, PatientTaskCard, TaskCard as TaskCardModel } from './taskCards';

interface Props {
  card: TaskCardModel;
  onOpen: (workflowId: string) => void;
  onManagePortals: (sourceSystem?: string) => void;
}

export default function TaskCard({ card, onOpen, onManagePortals }: Props) {
  if (card.kind === 'operational') return <OperationalCard card={card} onManagePortals={onManagePortals} />;
  return <PatientCard card={card} onOpen={onOpen} />;
}

// The name is ALWAYS the way in. The launch gate (`Open in <portal>` before the fields unlock)
// lives inside the slideout now — the card's only job is to open it.
function PatientCard({ card, onOpen }: { card: PatientTaskCard; onOpen: (workflowId: string) => void }) {
  return (
    <Card data-testid='task-card' data-kind='patient'>
      <Header>
        <TitleRow>
          <TitleLink type='button' onClick={() => onOpen(card.workflowId)} data-testid='task-card-open'>
            {card.title}
          </TitleLink>
          {card.badge && <SourceBadge data-testid='task-card-badge'>{card.badge}</SourceBadge>}
        </TitleRow>
        {card.createdAt && <Created>Created {simpleDateAtTime(card.createdAt)}</Created>}
      </Header>
      <Body>{card.body}</Body>
    </Card>
  );
}

function OperationalCard({
  card,
  onManagePortals,
}: {
  card: OperationalTaskCard;
  onManagePortals: (sourceSystem?: string) => void;
}) {
  return (
    <Card $operational data-testid='task-card' data-kind='operational'>
      <Header>
        <TitleRow>
          <WarningMark aria-hidden>
            <ErrorStatusIcon width={20} height={20} color={colors.yellow100} />
          </WarningMark>
          <Title data-testid='task-card-title'>
            <ActionNeeded>Action needed</ActionNeeded>: {card.title}
          </Title>
        </TitleRow>
      </Header>
      <OperationalBody>{card.body}</OperationalBody>
      <OperationalActions>
        {/* Both operational cards route here — and the rejected-password one deliberately
            offers no retry: retrying a rejected password is how an account gets locked out. */}
        <ManagePortalsButton
          size='small'
          onClick={() => onManagePortals(card.sourceSystem)}
          data-testid='task-card-manage-portals'>
          Manage Connected Portals
        </ManagePortalsButton>
      </OperationalActions>
    </Card>
  );
}

const Card = styled.div<{ $operational?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $operational }) => ($operational ? 12 : 8)}px;
  padding: 24px;
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  background-color: ${({ theme, $operational }) => ($operational ? theme.colors.yellow5 : theme.colors.white)};
  border: 1px solid ${({ theme, $operational }) => ($operational ? theme.colors.yellow100 : theme.colors.black10)};
`;

// The icon sits 12px off the title; the circle itself carries the border's amber.
const WarningMark = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  margin-right: 4px;
`;

// The design's token, darker than the icon/border amber on purpose — text at yellow100 fails
// contrast on the tinted card. The var isn't defined in index.css yet, so the fallback carries.
const ActionNeeded = styled.span`
  color: var(--secondary-orangeHover, #996000);
`;

const OperationalBody = styled(Body)`
  font-size: 12px;
  line-height: 20px;
`;

const ManagePortalsButton = styled(Button)`
  font-weight: 400;
`;

// The card's own gap gives 12px; this tops it up to the drawn 20px above the button.
const OperationalActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled(LabelBold)`
  font-size: 16px;
`;

const TitleLink = styled.button`
  font-family: ${({ theme }) => theme.textStyles.base.fontFamily};
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.blue100};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  text-decoration: underline;
`;

const SourceBadge = styled(BodySmall)`
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  background-color: ${({ theme }) => theme.colors.black10};
  color: ${({ theme }) => theme.colors.black75};
`;

const Created = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.black50};
`;
